import { useParams, useSearchParams } from 'next/navigation';

import type { Locale } from '@core/Entities/Locale/Locale.entity';
import type { FilterParams } from '@core/Entities/Search/QueryParams.entity';
import { searchParamsToObject } from '@core/Services/Search/Utils/FilterParamsFromUrl';
import { filtersSlugToObject } from '@core/Services/Search/Utils/FiltersSlugToObject';
import { mergeSlugAndSearchParams } from '@core/Services/Search/Utils/MergeSlugAndSearchParams';
import { addDefaultSortingIfMissing } from '@core/Utils/SearchFilters/AddDefaultSortingIfMissing';
import { createFiltersList } from '@core/Utils/SearchFilters/CreateFiltersList';

export const useFilterListParams = ({
  locale,
  pageSpecificFilters,
}: {
  locale: Locale;
  pageSpecificFilters?: FilterParams;
}) => {
  const { filtersSlug } = useParams();
  const searchParams = useSearchParams();

  return deriveFiltersList({
    filtersSlug: filtersSlug as string[],
    searchParams,
    locale,
    pageSpecificFilters,
  });
};

const deriveFiltersList = ({
  filtersSlug,
  searchParams,
  locale,
  pageSpecificFilters,
}: {
  filtersSlug: string[];
  searchParams: URLSearchParams;
  locale: Locale;
  pageSpecificFilters?: FilterParams;
}) => {
  const slugParams = filtersSlugToObject(filtersSlug);

  const combinedParams = mergeSlugAndSearchParams(
    { ...(pageSpecificFilters ?? {}), ...slugParams },
    searchParamsToObject(searchParams),
  );

  const combinedParamsWithSorting = addDefaultSortingIfMissing(combinedParams, locale);

  return createFiltersList(combinedParamsWithSorting);
};
